import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Card, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import ScheduleService, { TimelineWeek } from '../../api/services/schedule.service';
import { describeError } from '../../api/services/payroll.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import { EmptyState, LoadingState } from '../../components/ui/States';
import { LineChart, LineWeek } from '../payroll/reportCharts';
import { count, shortDate, weekdayShort } from '../payroll/payrollUI';
import SchedulePanel from './SchedulePanel';
import { isoDay, shiftWeek, weekEndingFor } from './scheduleMath';

// The sheet's "Capacity Plan & Graph": what happened, what is expected and
// what the roster can carry, for as long as there are numbers. Week by week
// the whole way back; day by day only around now, because the years before
// statements were kept by the week.

type Metric = 'stops' | 'packages';
type Grain = 'week' | 'day';
const DAILY_WEEKS = 8; // either side of this week

const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', py: 0.5, px: 1.25 } as const;
const opt = (n: number | null | undefined) => (n === null || n === undefined ? '—' : count(n));

const ScheduleTimeline: React.FC = () => {
  const navigate = useNavigate();
  const [weeks, setWeeks] = useState<TimelineWeek[] | null>(null);
  const [thisWeek, setThisWeek] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [metric, setMetric] = useState<Metric>('stops');
  const [grain, setGrain] = useState<Grain>('week');

  useEffect(() => {
    ScheduleService.timeline()
      .then((body) => { setWeeks(body.weeks); setThisWeek(body.thisWeek.slice(0, 10)); })
      .catch((err) => { setError(describeError(err, 'Could not load the timeline.')); setWeeks([]); });
  }, []);

  const max = metric === 'stops' ? 'maxStops' : 'maxPackages';
  const name = metric === 'stops' ? 'Stops' : 'Packages';

  const chart = useMemo(() => {
    if (!weeks) return null;
    if (grain === 'week') {
      return {
        slots: weeks.map((w): LineWeek => ({ key: w.weekEnding.slice(0, 10), label: shortDate(w.weekEnding) })),
        actual: weeks.map((w) => (w.status === 'actual' ? w[metric] : null)),
        forecast: weeks.map((w) => (w.status === 'forecast' ? w[metric] : null)),
        capacity: weeks.map((w) => w[max]),
        lastYear: weeks.map((w) => (w.lastYear ? w.lastYear[metric] : null)),
      };
    }
    const from = shiftWeek(thisWeek, -DAILY_WEEKS);
    const to = shiftWeek(thisWeek, DAILY_WEEKS);
    const near = weeks.filter((w) => w.weekEnding.slice(0, 10) >= from && w.weekEnding.slice(0, 10) <= to);
    const days = near.flatMap((w) => w.days.map((d) => ({ ...d, status: w.status })));
    return {
      slots: days.map((d): LineWeek => ({ key: d.date.slice(0, 10), label: `${weekdayShort(d.date)} ${shortDate(d.date)}` })),
      actual: days.map((d) => (d.status === 'actual' ? d[metric] : null)),
      forecast: days.map((d) => (d.status === 'forecast' ? d[metric] : null)),
      capacity: days.map((d) => d[max]),
      lastYear: null,
    };
  }, [weeks, grain, metric, max, thisWeek]);

  const rows = useMemo(
    () => (weeks || []).filter((w) => w.status !== 'none' || w.maxStops !== null).slice().reverse(),
    [weeks],
  );
  // The list runs far into the future and far into the past; start at now.
  const current = useRef<HTMLTableRowElement>(null);
  useEffect(() => { current.current?.scrollIntoView?.({ block: 'center' }); }, [rows]);

  const open = (key: string) => navigate(`/schedule?week=${key}`);
  // A day on the chart opens the week it is in.
  const openDay = (key: string) => open(weekEndingFor(new Date(`${key}T12:00:00`)));
  const today = useMemo(() => isoDay(new Date()), []);

  const left = (w: TimelineWeek, key: Metric) => {
    const cap = key === 'stops' ? w.maxStops : w.maxPackages;
    const v = w[key];
    return cap === null || v === null ? null : cap - v;
  };

  return (
    <PageBody fluid>
      <SchedulePanel />
      <PageHeader
        title="Timeline"
        subtitle="Actual volume, what is expected next, and what the schedule can carry."
        actions={(
          <Stack direction="row" gap={1}>
            <ToggleButtonGroup size="small" exclusive value={metric} onChange={(_, m) => m && setMetric(m)}>
              <ToggleButton value="stops" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>Stops</ToggleButton>
              <ToggleButton value="packages" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>Packages</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={grain} onChange={(_, g) => g && setGrain(g)}>
              <ToggleButton value="week" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>Weeks</ToggleButton>
              <ToggleButton value="day" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>Days</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {weeks === null ? <LoadingState /> : !rows.length ? (
        <EmptyState title="Nothing to show yet" description="Each statement you upload adds to this. So does importing past weeks in Weekly volume." />
      ) : (
        <Stack gap={2}>
          {chart && chart.slots.length > 1 && (
            <Card sx={{ p: 2 }}>
              <LineChart
                bare
                xLabels
                zero
                title={`${name} a ${grain}`}
                hint={grain === 'week' ? 'From the first week on record.' : `${DAILY_WEEKS} weeks either side of this one, where statements have days.`}
                weeks={chart.slots}
                series={[
                  { series: 'actual', label: 'Actual', values: chart.actual },
                  { series: 'forecast', label: 'Expected', values: chart.forecast },
                  { series: 'capacity', label: 'Capacity', values: chart.capacity },
                  ...(chart.lastYear ? [{ series: 'lastYear' as const, label: 'Last year', values: chart.lastYear }] : []),
                ]}
                format={(n) => count(Math.round(n))}
                height={260}
                onPick={grain === 'week' ? open : openDay}
                slotName={(w) => (grain === 'week' ? `Week ending ${w.label}` : w.label)}
              />
            </Card>
          )}

          <Card sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ px: 1.25 }}>Week ending</TableCell>
                    <TableCell sx={{ px: 1.25 }}>Source</TableCell>
                    {['Stops', 'Max stops', 'Stops left', 'Packages', 'Max pkgs', 'Pkgs left', 'Stops last yr', 'Pkgs last yr'].map((h) => (
                      <TableCell key={h} align="right" sx={{ px: 1.25 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((w) => {
                    const key = w.weekEnding.slice(0, 10);
                    const stopsLeft = left(w, 'stops');
                    const packagesLeft = left(w, 'packages');
                    return (
                      <TableRow
                        key={key} hover tabIndex={0} sx={{ cursor: 'pointer' }}
                        ref={key === thisWeek ? current : undefined}
                        selected={key === thisWeek}
                        onClick={() => open(key)}
                        onKeyDown={(e) => { if (e.key === 'Enter') open(key); }}
                      >
                        <TableCell sx={num}>{shortDate(w.weekEnding)}, {key.slice(0, 4)}</TableCell>
                        <TableCell sx={{ ...num, color: 'text.secondary' }}>
                          {w.status === 'actual' ? 'Actual' : w.status === 'forecast' ? 'Expected' : key < today ? 'No data' : '—'}
                        </TableCell>
                        <TableCell align="right" sx={num}>{opt(w.stops)}</TableCell>
                        <TableCell align="right" sx={num}>{opt(w.maxStops)}</TableCell>
                        <TableCell align="right" sx={{ ...num, fontWeight: 700, color: stopsLeft !== null && stopsLeft < 0 ? 'error.main' : undefined }}>{opt(stopsLeft)}</TableCell>
                        <TableCell align="right" sx={num}>{opt(w.packages)}</TableCell>
                        <TableCell align="right" sx={num}>{opt(w.maxPackages)}</TableCell>
                        <TableCell align="right" sx={{ ...num, fontWeight: 700, color: packagesLeft !== null && packagesLeft < 0 ? 'error.main' : undefined }}>{opt(packagesLeft)}</TableCell>
                        <TableCell align="right" sx={{ ...num, color: 'text.secondary' }}>{opt(w.lastYear?.stops)}</TableCell>
                        <TableCell align="right" sx={{ ...num, color: 'text.secondary' }}>{opt(w.lastYear?.packages)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Stack>
      )}
    </PageBody>
  );
};

export default ScheduleTimeline;
